import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MDBContainer, MDBBtn, MDBInput, MDBRow, MDBCol, MDBCard, MDBCardBody } from "mdb-react-ui-kit";
import axios from "axios";
import "./UpdateProductPage.css";

const UpdateProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  // Local Product State
  const [localProduct, setLocalProduct] = useState({
    title: "",
    description: "",
    sku: "",
    price: "",
    quantity: "",
    seo_tags: "",
    category: "",
    condition: "",
    brand: "",
    color: "",
    images: []
  });
  
  // Shopify State
  const [shopifyData, setShopifyData] = useState({
    shopify_product_id: "",
    shopify_variant_id: "",
    inventory_item_id: "",
    sku: "",
    price: "",
    quantity: "",
    vendor: "",
    product_type: "",
    barcode: "",
    compare_at_price: ""
  });
  
  const [hasShopify, setHasShopify] = useState(false);
  const [updateStatus, setUpdateStatus] = useState({ show: false, message: "", type: "" });
  
  // Fetch all product data
  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true);
      try {
        // 1️⃣ Fetch Local Product Details
        const productRes = await axios.get(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/product/${id}`
        );
        
        if (productRes.data.success && productRes.data.product) {
          const product = productRes.data.product;
          const images = productRes.data.images || [];
          
          setLocalProduct({
            title: product.title || "",
            description: product.description || "",
            sku: product.sku || "",
            price: product.price || "",
            quantity: product.quantity || "",
            seo_tags: product.seo_tags || "",
            category: product.category || "",
            condition: product.condition || "",
            brand: product.brand || "",
            color: product.color || "",
            images: images.map(img => img.image_url)
          });
        }
        
        // 2️⃣ Fetch Shopify Details
        try {
          const shopifyRes = await axios.get(
            `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify-product/${id}`
          );
          
          if (shopifyRes.data.success && shopifyRes.data.data) {
            const shopify = shopifyRes.data.data;
            setHasShopify(true);
            setShopifyData({
              shopify_product_id: shopify.shopify_product_id || "",
              shopify_variant_id: shopify.shopify_variant_id || "",
              inventory_item_id: shopify.inventory_item_id || "",
              sku: shopify.sku || "",
              price: shopify.price || "",
              quantity: shopify.quantity || "",
              vendor: shopify.vendor || "",
              product_type: shopify.product_type || "",
              barcode: shopify.barcode || "",
              compare_at_price: shopify.compare_at_price || ""
            });
          }
        } catch (err) {
          console.log("No Shopify data found for this product");
          setHasShopify(false);
        }
        
      } catch (error) {
        console.error("Error fetching product:", error);
        showMessage("Failed to load product data", "error");
      } finally {
        setLoading(false);
      }
    };
    
    fetchProductData();
  }, [id]);
  
  const showMessage = (message, type = "success") => {
    setUpdateStatus({ show: true, message, type });
    setTimeout(() => {
      setUpdateStatus({ show: false, message: "", type: "" });
    }, 3000);
  };
  
  const handleLocalChange = (field, value) => {
    setLocalProduct(prev => ({ ...prev, [field]: value }));
  };
  
  const handleShopifyChange = (field, value) => {
    setShopifyData(prev => ({ ...prev, [field]: value }));
  };
  
  // Update Local Product
  const updateLocalProduct = async () => {
    const company_id = localStorage.getItem("company_id");
    
    const payload = {
      company_id: parseInt(company_id),
      title: localProduct.title,
      sku: localProduct.sku,
      description: localProduct.description,
      seo_tags: localProduct.seo_tags,
      price: parseFloat(localProduct.price),
      quantity: parseInt(localProduct.quantity),
      images: localProduct.images
    };
    
    const response = await axios.put(
      `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/update-product/${id}`,
      payload
    );
    
    return response.data;
  };
  
  // Update Shopify Product - Directly on Shopify Store
  const updateShopifyProduct = async () => {
    if (!hasShopify) return null;
    
    const payload = {
      title: localProduct.title,
      description: localProduct.description,
      sku: shopifyData.sku,
      price: shopifyData.price,
      quantity: shopifyData.quantity,
      vendor: shopifyData.vendor,
      product_type: shopifyData.product_type,
      compare_at_price: shopifyData.compare_at_price
    };
    
    const response = await axios.put(
      `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify-store-update/${id}`,
      payload
    );
    
    return response.data;
  };
  
  // Handle Complete Update
  const handleUpdateAll = async () => {
    setUpdating(true);
    try {
      // 1️⃣ Update Local Database
      await updateLocalProduct();
      showMessage("✅ Local database updated successfully!", "success");
      
      // 2️⃣ Update Shopify Store (if exists)
      if (hasShopify) {
        await updateShopifyProduct();
        showMessage("✅ Shopify store updated successfully!", "success");
      }
      
      showMessage("All changes saved successfully!", "success");
      
      setTimeout(() => {
        navigate("/products");
      }, 2000);
      
    } catch (error) {
      console.error("Update error:", error);
      showMessage(`Update failed: ${error.response?.data?.error || error.message}`, "error");
    } finally {
      setUpdating(false);
    }
  };
  
  if (loading) {
    return (
      <MDBContainer className="py-5 text-center">
        <h4>Loading product data...</h4>
      </MDBContainer>
    );
  }
  
  return (
    <MDBContainer className="py-4">
      <h2 className="mb-4">Update Product</h2>
      
      {/* Status Message */}
      {updateStatus.show && (
        <div className={`alert alert-${updateStatus.type === "error" ? "danger" : "success"} mb-3`}>
          {updateStatus.message}
        </div>
      )}
      
      {/* Local Product Section */}
      <MDBCard className="mb-4">
        <MDBCardBody>
          <h4 className="mb-3">📦 Local Product Details</h4>
          <MDBRow>
            <MDBCol md="6">
              <MDBInput
                label="Title"
                value={localProduct.title}
                onChange={(e) => handleLocalChange("title", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label="SKU"
                value={localProduct.sku}
                onChange={(e) => handleLocalChange("sku", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="12">
              <MDBInput
                label="Description"
                value={localProduct.description}
                onChange={(e) => handleLocalChange("description", e.target.value)}
                className="mb-3"
                as="textarea"
                rows="3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Price ($)"
                type="number"
                value={localProduct.price}
                onChange={(e) => handleLocalChange("price", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Quantity"
                type="number"
                value={localProduct.quantity}
                onChange={(e) => handleLocalChange("quantity", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Category"
                value={localProduct.category}
                onChange={(e) => handleLocalChange("category", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Condition"
                value={localProduct.condition}
                onChange={(e) => handleLocalChange("condition", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Brand"
                value={localProduct.brand}
                onChange={(e) => handleLocalChange("brand", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="3">
              <MDBInput
                label="Color"
                value={localProduct.color}
                onChange={(e) => handleLocalChange("color", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
            <MDBCol md="6">
              <MDBInput
                label="SEO Tags"
                value={localProduct.seo_tags}
                onChange={(e) => handleLocalChange("seo_tags", e.target.value)}
                className="mb-3"
              />
            </MDBCol>
          </MDBRow>
        </MDBCardBody>
      </MDBCard>
      
      {/* Shopify Section */}
      {hasShopify && (
        <MDBCard className="mb-4 border-success">
          <MDBCardBody>
            <h4 className="mb-3 text-success">🛍️ Shopify Listing</h4>
            <MDBRow>
              <MDBCol md="4">
                <MDBInput
                  label="Shopify Product ID"
                  value={shopifyData.shopify_product_id}
                  disabled
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="4">
                <MDBInput
                  label="Variant ID"
                  value={shopifyData.shopify_variant_id}
                  disabled
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="4">
                <MDBInput
                  label="SKU"
                  value={shopifyData.sku}
                  onChange={(e) => handleShopifyChange("sku", e.target.value)}
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="3">
                <MDBInput
                  label="Price ($)"
                  type="number"
                  value={shopifyData.price}
                  onChange={(e) => handleShopifyChange("price", e.target.value)}
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="3">
                <MDBInput
                  label="Quantity"
                  type="number"
                  value={shopifyData.quantity}
                  onChange={(e) => handleShopifyChange("quantity", e.target.value)}
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="3">
                <MDBInput
                  label="Vendor"
                  value={shopifyData.vendor}
                  onChange={(e) => handleShopifyChange("vendor", e.target.value)}
                  className="mb-3"
                />
              </MDBCol>
              <MDBCol md="3">
                <MDBInput
                  label="Product Type"
                  value={shopifyData.product_type}
                  onChange={(e) => handleShopifyChange("product_type", e.target.value)}
                  className="mb-3"
                />
              </MDBCol>
            </MDBRow>
          </MDBCardBody>
        </MDBCard>
      )}
      
      {/* Action Buttons */}
      <div className="d-flex gap-3 mt-4">
        <MDBBtn color="success" onClick={handleUpdateAll} disabled={updating}>
          {updating ? "Saving Changes..." : "💾 Save All Changes"}
        </MDBBtn>
        <MDBBtn color="secondary" onClick={() => navigate("/products")}>
          Cancel
        </MDBBtn>
      </div>
    </MDBContainer>
  );
};

export default UpdateProductPage;